import React from 'react'
import {
  MoreVertical,
  Pencil,
  Copy,
  Zap,
  ZapOff,
  Trash2,
  Pause,
  Play,
  RefreshCw,
  ExternalLink,
  FolderOpen,
  Download,
} from 'lucide-react'
import { cn } from '../UI/cn'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../UI/BaseDropdown/dropdown-base'
import { useModalDepth } from '../UI/Modal/ModalPrimitive'
import { getDropdownZIndex } from '../../styles/z-index'

interface ActionItem {
  label: string
  icon: React.ComponentType<{ size?: number; className?: string }>
  onClick: () => void
  variant?: 'default' | 'destructive'
  disabled?: boolean
  disabledReason?: string
  hasSeparatorAfter?: boolean
}

interface ActionsDropdownProps {
  actions: ActionItem[]
  disabled?: boolean
  className?: string
  containerWidth?: number
}

export function ActionsDropdown({
  actions,
  className,
  disabled,
  containerWidth = 120,
}: ActionsDropdownProps) {
  const modalDepth = useModalDepth()
  const zIndex = React.useMemo(
    () => getDropdownZIndex(modalDepth),
    [modalDepth]
  )

  return (
    <div className={cn('relative flex justify-end', className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={disabled}>
          <button
            type="button"
            className={cn(
              'flex items-center justify-center w-5 h-5 text-gray-500 hover:text-gray-700 focus:outline-none focus:text-gray-700',
              {
                'opacity-50 cursor-not-allowed': disabled,
              }
            )}
            aria-label="Actions"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreVertical
              className={cn({
                'cursor-not-allowed': disabled,
              })}
              size={20}
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          style={{ minWidth: containerWidth, zIndex }}
          className="bg-white border-line-focus"
        >
          {actions.map((action, index) => (
            <React.Fragment key={index}>
              <DropdownMenuItem
                disabled={action.disabled}
                title={action.disabled ? action.disabledReason : undefined}
                className={cn(
                  'flex items-center gap-2 cursor-pointer',
                  action.disabled && 'opacity-50 cursor-not-allowed'
                )}
                onSelect={() => {
                  if (!action.disabled) {
                    action.onClick()
                  }
                }}
              >
                <action.icon
                  size={20}
                  className="flex-shrink-0 text-gray-500"
                />
                <span className="truncate">{action.label}</span>
              </DropdownMenuItem>
              {action.hasSeparatorAfter && <DropdownMenuSeparator />}
            </React.Fragment>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

// Pre-configured actions for common use cases
export const createEditAction = (
  onClick: () => void,
  label = 'Edit'
): ActionItem => ({
  label,
  icon: Pencil,
  onClick,
})

export const createCopyAction = (onClick: () => void): ActionItem => ({
  label: 'Copy',
  icon: Copy,
  onClick,
})

export const createPauseAction = (onClick: () => void): ActionItem => ({
  label: 'Pause',
  icon: Pause,
  onClick,
})

export const createPlayAction = (onClick: () => void): ActionItem => ({
  label: 'Resume',
  icon: Play,
  onClick,
})

export const createSyncAction = (
  onClick: () => void,
  disabled?: boolean
): ActionItem => ({
  label: 'Sync',
  icon: RefreshCw,
  onClick,
  disabled,
})

export const createExternalAction = (
  label: string,
  onClick: () => void
): ActionItem => ({
  label,
  icon: ExternalLink,
  onClick,
})

export const createDownloadAction = (
  label: string,
  onClick: () => void
): ActionItem => ({
  label,
  icon: Download,
  onClick,
})

export const createToggleActiveAction = (
  onClick: () => void,
  isActive: boolean,
  isDisabled = false
): ActionItem => ({
  label: isActive ? 'Disable' : 'Enable',
  icon: isActive ? ZapOff : Zap,
  onClick,
  disabled: isDisabled,
  hasSeparatorAfter: true,
})

export const createDeleteAction = (onClick: () => void): ActionItem => ({
  label: 'Delete',
  icon: Trash2,
  onClick,
  variant: 'destructive' as const,
})

export const createManageFilesAction = (onClick: () => void): ActionItem => ({
  label: 'Manage Files',
  icon: FolderOpen,
  onClick,
})
